import { useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";

const TIMEOUT_DURATION = 8 * 60 * 60 * 1000; // 8 hours
const WARNING_BEFORE_TIMEOUT = 5 * 60 * 1000; // Show warning 5 minutes before timeout

interface UseSessionTimeoutOptions {
  onWarning?: () => void;
  onTimeout?: () => void;
}

export function useSessionTimeout(options?: UseSessionTimeoutOptions) {
  const { user } = useAuth();
  const [showWarning, setShowWarning] = useState(false);
  const [remainingTime, setRemainingTime] = useState(0);
  
  // Use refs to avoid circular dependencies
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const warningRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const countdownRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const lastActivityRef = useRef<number>(Date.now());
  const isWarningShownRef = useRef(false);
  const optionsRef = useRef(options);
  
  // Keep options ref up to date
  useEffect(() => {
    optionsRef.current = options;
  }, [options]);

  const clearAllTimers = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (warningRef.current) {
      clearTimeout(warningRef.current);
      warningRef.current = null;
    }
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
      countdownRef.current = null;
    }
  };

  const handleTimeout = async () => {
    clearAllTimers();
    setShowWarning(false);
    isWarningShownRef.current = false;
    
    // Session timeout sign-out — the server-side trigger on auth.sessions
    // will automatically log this as a 'logout' event in audit.logs.
    await supabase.auth.signOut();
    optionsRef.current?.onTimeout?.();
  };

  const startTimers = () => {
    clearAllTimers();
    lastActivityRef.current = Date.now();
    isWarningShownRef.current = false;
    setShowWarning(false);

    // Set warning timer
    warningRef.current = setTimeout(() => {
      isWarningShownRef.current = true;
      setShowWarning(true);
      setRemainingTime(Math.floor(WARNING_BEFORE_TIMEOUT / 1000));
      optionsRef.current?.onWarning?.();

      // Start countdown
      countdownRef.current = setInterval(() => {
        setRemainingTime((prev) => {
          if (prev <= 1) {
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }, TIMEOUT_DURATION - WARNING_BEFORE_TIMEOUT);

    // Set logout timer
    timeoutRef.current = setTimeout(() => {
      handleTimeout();
    }, TIMEOUT_DURATION);
  };

  const extendSession = () => {
    setShowWarning(false);
    isWarningShownRef.current = false;
    startTimers();
  };

  // Handle activity - only reset if warning is NOT shown
  const handleActivity = () => {
    if (!user || isWarningShownRef.current) return;
    
    const now = Date.now();
    // Debounce: only reset if more than 1 second since last activity
    if (now - lastActivityRef.current > 1000) {
      lastActivityRef.current = now;
      startTimers();
    }
  };

  useEffect(() => {
    if (!user) {
      clearAllTimers();
      setShowWarning(false);
      isWarningShownRef.current = false;
      return;
    }

    // Activity events to track
    const events = ["mousedown", "keydown", "scroll", "touchstart", "click"];

    // Add event listeners
    events.forEach((event) => {
      window.addEventListener(event, handleActivity, { passive: true });
    });

    // Initial timer setup
    startTimers();

    return () => {
      events.forEach((event) => {
        window.removeEventListener(event, handleActivity);
      });
      clearAllTimers();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  return {
    showWarning,
    remainingTime,
    extendSession,
  };
}
